import React, { useState } from 'react';
import {
  Card,
  Button,
  Table,
  Tag,
  Select,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  Badge,
  Tooltip,
  Space,
  Pagination,
  ConfigProvider
} from 'antd';
import { useQuery } from 'react-query';
import { debtService, Debt } from '../../services/debtService';
import { customerService, Customer } from '../../services/customerService';
import DebtDetailDrawer from '../../components/debts/DebtDetailModal';
import { calculateGroupedDebtStatus, getDebtStatusColor, getDebtStatusText } from '../../utils/debtStatusUtils';
import {
  FilterOutlined
} from '@ant-design/icons';
import styles from './DebtsPage.module.css';
import locale from 'antd/locale/vi_VN';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

const statusIcon = {
  unpaid: <i className="fas fa-exclamation-circle" />,
  partial: <i className="fas fa-clock" />,
  paid: <i className="fas fa-check-circle" />,
};

const DebtsPage: React.FC = () => {
  const [selectedDebt, setSelectedDebt] = useState<Debt | null>(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all'); // Thêm state cho bộ lọc trạng thái
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  // State lưu debts của khách hàng đang xem chi tiết
  const [customerDebts, setCustomerDebts] = useState<Debt[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Lấy danh sách khách hàng cho filter
  const { data: customers = [], isLoading: isLoadingCustomers } = useQuery(
    'customers-for-select',
    () => customerService.getCustomersForSelect()
  );

  // Lấy danh sách công nợ
  const { data: debts = [], isLoading, refetch } = useQuery(
    'debts',
    () => debtService.getDebts()
  );

  // Gom nhóm công nợ theo khách hàng
  const groupedDebts = React.useMemo(() => {
    const map: Record<string, { customer: any, totalDebt: number, totalPaid: number, remain: number, status: string, latestCreated: string } > = {};
    debts.forEach((d: any) => {
      const key = String(d.customer_id);
      if (!map[key]) {
        // Sử dụng thông tin khách hàng từ API response thay vì customerMap
        const customerInfo = {
          id: d.customer_id,
          name: d.customer_name || `Khách hàng ${d.customer_id}`,
          code: d.customer_code || d.customer_id,
          phone: d.customer_phone,
          address: d.customer_address
        };
        map[key] = {
          customer: customerInfo,
          totalDebt: 0,
          totalPaid: 0,
          remain: 0,
          status: 'unpaid',
          latestCreated: d.created_at
        };
      }
      map[key].totalDebt += d.debt_amount;
      map[key].totalPaid += d.paid_amount;
      map[key].remain += (d.debt_amount - d.paid_amount);
      if (d.created_at > map[key].latestCreated) map[key].latestCreated = d.created_at;
    });

    // Xác định trạng thái tổng hợp sau khi đã tính toán xong
    Object.values(map).forEach((group) => {
      const statusInfo = calculateGroupedDebtStatus([
        { debt_amount: group.totalDebt, paid_amount: group.totalPaid }
      ]);
      group.status = statusInfo.status;
      group.remain = statusInfo.remain;
    });

    return Object.entries(map).map(([customer_id, v]) => ({
      customer_id,
      ...v
    }));
  }, [debts]);

  // Filtered data
  const filteredDebts = React.useMemo(() => {
    return groupedDebts.filter((d) => {
      // Filter theo trạng thái
      if (statusFilter !== 'all' && d.status !== statusFilter) {
        return false;
      }
      
      // Filter theo search text
      if (searchText) {
        // Nếu searchText là số, so sánh với customer_id
        if (!isNaN(Number(searchText))) {
          return String(d.customer_id).includes(searchText);
        }
        // Nếu searchText là text, so sánh với tên khách hàng hoặc mã khách hàng
        if (d.customer) {
          const customerName = d.customer.name?.toLowerCase() || '';
          const customerCode = d.customer.code?.toLowerCase() || '';
          const searchLower = searchText.toLowerCase();
          return customerName.includes(searchLower) || customerCode.includes(searchLower);
        }
        return false;
      }
      return true;
    });
  }, [groupedDebts, statusFilter, searchText]);

  // Thống kê theo bộ lọc hiện tại
  const filteredStats = React.useMemo(() => {
    const totalDebt = filteredDebts.reduce((sum: number, d: any) => sum + d.totalDebt, 0);
    const totalPaid = filteredDebts.reduce((sum: number, d: any) => sum + d.totalPaid, 0);
    const totalRemain = filteredDebts.reduce((sum: number, d: any) => sum + d.remain, 0);
    return { totalDebt, totalPaid, totalRemain };
  }, [filteredDebts]);

  // Thống kê
  const totalDebt = filteredStats.totalDebt;
  const totalPaid = filteredStats.totalPaid;
  const totalRemain = filteredStats.totalRemain;

  // Mapping customerId -> customer object for fast lookup
  const customerMap = React.useMemo(() => {
    const map: Record<string, Customer> = {};
    customers.forEach((c: Customer) => {
      map[c.id] = c;
    });
    return map;
  }, [customers]);

  // Đếm đúng số khách nợ
  const totalCustomers = filteredDebts.length;

  // Pagination
  const pagedDebts = React.useMemo(() => {
    const start = (pagination.current - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredDebts.slice(start, end);
  }, [filteredDebts, pagination]);

  const handleViewDetail = async (r: any) => {
    setLoadingDetail(true);
    // Lọc tất cả debts của khách hàng này từ debts gốc
    const debtsOfCustomer = debts.filter((d: Debt) => String(d.customer_id) === String(r.customer_id));
    // Fetch chi tiết từng khoản nợ (bao gồm payments)
    const allDebtsWithPayments = await Promise.all(
      debtsOfCustomer.map(async (d) => {
        try {
          const detail = await debtService.getDebtDetail(d.id);
          return { ...d, payments: detail.payments };
        } catch {
          return { ...d, payments: [] };
        }
      })
    );
    setCustomerDebts(allDebtsWithPayments);
    setSelectedDebt({ ...allDebtsWithPayments[0], customer_id: r.customer_id });
    setLoadingDetail(false);
  };

  const getSummaryStats = () => {
    return (
      <div className={styles.statsContainer}>
        <Row gutter={[24, 16]} className={styles.statsRow}>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#3b82f6' }}>
                  <i className="fas fa-users" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Tổng khách nợ"
                    value={totalCustomers}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#f59e0b' }}>
                  <i className="fas fa-dollar-sign" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Tổng số nợ"
                    value={totalDebt}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                    formatter={(value) => value?.toLocaleString('vi-VN')}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#10b981' }}>
                  <i className="fas fa-check-circle" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Đã trả"
                    value={totalPaid}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                    formatter={(value) => value?.toLocaleString('vi-VN')}
                  />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className={styles.statCard} hoverable>
              <div className={styles.statContent}>
                <div className={styles.statIcon} style={{ background: '#ef4444' }}>
                  <i className="fas fa-exclamation-circle" />
                </div>
                <div className={styles.statInfo}>
                  <Statistic
                    title="Còn lại"
                    value={totalRemain}
                    valueStyle={{ color: '#1f2937', fontSize: '24px', fontWeight: 'bold' }}
                    formatter={(value) => value?.toLocaleString('vi-VN')}
                  />
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  const columns = [
    {
      title: 'Khách hàng',
      dataIndex: 'customer_id',
      key: 'customer_id',
      width: 200,
      fixed: 'left' as const,
      sorter: (a: any, b: any) => {
        const nameA = a.customer?.name || `Khách hàng ${a.customer_id}`;
        const nameB = b.customer?.name || `Khách hàng ${b.customer_id}`;
        return nameA.localeCompare(nameB, 'vi');
      },
      render: (_: any, r: any) => {
        const customer = r.customer;
        return customer ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ 
              fontWeight: '600', 
              fontSize: '15px',
              color: '#1f2937',
              lineHeight: '1.2'
            }}>
              {customer.name}
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#6b7280',
              fontWeight: '400'
            }}>
              Mã KH: {customer.code}
            </div>
            {customer.phone && (
              <div style={{ 
                fontSize: '12px', 
                color: '#6b7280',
                fontWeight: '400'
              }}>
                {customer.phone}
              </div>
            )}
          </div>
        ) : (
          <div style={{ 
            fontWeight: '600', 
            fontSize: '15px',
            color: '#1f2937'
          }}>
            {`Khách hàng ${r.customer_id}`}
          </div>
        );
      },
    },
    {
      title: 'Thông tin nợ',
      key: 'debt_info',
      width: 180,
      render: (_: any, r: any) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '13px',
            color: '#374151'
          }}>
            <span style={{ fontWeight: '500' }}>Tổng nợ:</span>
            <span style={{ fontWeight: '600', color: '#f59e0b' }}>
              {r.totalDebt.toLocaleString('vi-VN')}đ
            </span>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '13px',
            color: '#374151'
          }}>
            <span style={{ fontWeight: '500' }}>Đã trả:</span>
            <span style={{ fontWeight: '600', color: '#059669' }}>
              {r.totalPaid.toLocaleString('vi-VN')}đ
            </span>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '14px',
            color: '#374151',
            borderTop: '1px solid #e5e7eb',
            paddingTop: '4px'
          }}>
            <span style={{ fontWeight: '600' }}>Còn lại:</span>
            <span style={{ fontWeight: '700', color: '#dc2626' }}>
              {r.remain.toLocaleString('vi-VN')}đ
            </span>
          </div>
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      sorter: (a: any, b: any) => {
        const statusOrder = { 'unpaid': 0, 'partial': 1, 'paid': 2 };
        return statusOrder[a.status] - statusOrder[b.status];
      },
      render: (v: string) => {
        const statusConfig = {
          unpaid: { 
            color: '#dc2626', 
            bgColor: '#fee2e2',
            text: 'Chưa trả',
            icon: 'fas fa-exclamation-circle'
          },
          partial: { 
            color: '#f59e0b', 
            bgColor: '#fef3c7',
            text: 'Còn nợ',
            icon: 'fas fa-clock'
          },
          paid: { 
            color: '#059669', 
            bgColor: '#d1fae5',
            text: 'Đã trả',
            icon: 'fas fa-check-circle'
          }
        };
        const config = statusConfig[v as keyof typeof statusConfig];
        return (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            padding: '6px 12px',
            borderRadius: '20px',
            backgroundColor: config.bgColor,
            color: config.color,
            fontSize: '12px',
            fontWeight: '500',
            width: 'fit-content'
          }}>
            <i className={config.icon} style={{ fontSize: '10px' }} />
            {config.text}
          </div>
        );
      },
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, r: any) => (
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <Tooltip title="Xem chi tiết">
            <Button
              type="text"
              icon={<i className="fas fa-eye" />}
              onClick={() => handleViewDetail(r)}
              size="small"
              style={{ 
                color: '#6b7280',
                border: 'none',
                boxShadow: 'none'
              }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <ConfigProvider locale={locale}>
      <div className={styles.container}>
        {getSummaryStats()}

        <Card className={styles.tableCard}>
          <div className={styles.tableHeader}>
            <div className={styles.searchSection}>
              <Search
                placeholder="Tìm kiếm khách hàng..."
                allowClear
                className={styles.searchInput}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onSearch={setSearchText}
                size="large"
                style={{ textAlign: 'left' }}
              />
            </div>
            <div className={styles.filterSection}>
              <Space size="middle" wrap>
                <Select
                  placeholder="Trạng thái nợ"
                  value={statusFilter}
                  onChange={v => setStatusFilter(v)}
                  allowClear
                  style={{ minWidth: 150 }}
                >
                  <Option value="all">Tất cả trạng thái</Option>
                  <Option value="unpaid">Chưa trả</Option>
                  <Option value="partial">Còn nợ</Option>
                  <Option value="paid">Đã trả</Option>
                </Select>
                {(statusFilter || searchText) && (
                  <Button
                    type="link"
                    icon={<FilterOutlined />}
                    onClick={() => {
                      setSearchText('');
                      setStatusFilter('all');
                      setPagination({ current: 1, pageSize: 20 });
                    }}
                    size="small"
                  >
                    Xóa bộ lọc
                  </Button>
                )}
              </Space>
            </div>
            <div className={styles.actionButtons}>
              <Space size="middle">
                <Tooltip title="Làm mới dữ liệu">
                  <Button
                    icon={<i className="fas fa-sync-alt" />}
                    onClick={() => refetch()}
                    className={styles.actionButton}
                  >
                    Làm mới
                  </Button>
                </Tooltip>
              </Space>
            </div>
          </div>

          <Table
            columns={columns}
            dataSource={pagedDebts}
            rowKey={r => r.customer_id}
            loading={isLoading || loadingDetail}
            className={styles.debtsTable}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: filteredDebts.length,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} trong ${total} khách nợ`,
              className: styles.tablePagination,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => setPagination({ current: page, pageSize })
            }}
            size="middle"
            scroll={{ x: 600 }}
          />
        </Card>
        {/* Modal chi tiết */}
        {selectedDebt && (
          <DebtDetailDrawer
            debt={selectedDebt}
            onClose={() => setSelectedDebt(null)}
            onPaid={() => {
              setSelectedDebt(null);
              refetch();
            }}
            customers={customers}
            allDebts={customerDebts}
          />
        )}
      </div>
    </ConfigProvider>
  );
};

export default DebtsPage; 